import { Injectable, Injector, OnDestroy } from '@angular/core';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';

import {
  createEditor,
  EditorJSONState,
  EMPTY_SELECTION,
  FocusPlugin,
  PlaceholderPlugin,
  RtPMPlugin,
  StateChangesPlugin,
  EditorMode,
} from '@recruitee/prosemirror';

import { ProsemirrorDefaultConfig } from '../../configs/default-config';
import { ProsemirrorViewboxConfig } from '../../configs/viewbox-config';
import { StateUpdated$ } from '../../consts';

interface CreateEditorOptions {
  placeholder?: string;
}

export enum ProsemirrorConfig {
  Default,
  ViewboxNotes,
}

const prosemirrorConfig = {
  [ProsemirrorConfig.Default]: ProsemirrorDefaultConfig,
  [ProsemirrorConfig.ViewboxNotes]: ProsemirrorViewboxConfig,
};

@Injectable()
export class ProsemirrorService implements OnDestroy {
  public editorView: EditorView;

  private destroyPlugins: () => void;

  constructor(private stateUpdated$: StateUpdated$, private injector: Injector) {}

  public ngOnDestroy() {
    this.destroyPlugins();
    this.editorView.destroy();
  }

  public getCoreEditorPlugins(options: CreateEditorOptions) {
    return [
      ...(options.placeholder ? [new PlaceholderPlugin(options.placeholder)] : []),
      new StateChangesPlugin(state => this.stateUpdated$.next(state)),
      new FocusPlugin(),
    ];
  }

  public updateState(jsonState: EditorJSONState) {
    const { schema, plugins, storedMarks } = this.editorView.state;
    const state = jsonState
      ? EditorState.fromJSON(this.editorView.state, {
          ...jsonState,
          selection: jsonState.selection || EMPTY_SELECTION,
        })
      : EditorState.create({ schema, plugins, storedMarks });
    this.editorView.setProps({ state });
  }

  public createEditor(
    element: HTMLElement,
    configKey: ProsemirrorConfig,
    options: {
      mode: EditorMode;
      initialState?: EditorJSONState | null;
      placeholder?: string;
    },
  ) {
    const config = this.injector.get(prosemirrorConfig[configKey]);
    const plugins: RtPMPlugin[] = [
      ...config.getRenderPlugins(),
      ...(EditorMode.Wysiwyg === options.mode
        ? [
            ...this.getCoreEditorPlugins({ placeholder: options.placeholder }),
            ...config.getEditorPlugins(),
          ]
        : []),
    ];

    const { editorView, schema, destroyPlugins, publicApi, buttons } = createEditor(
      element,
      plugins,
      config,
      {
        mode: options.mode,
        initialState: options.initialState,
      },
    );

    this.editorView = editorView;
    this.destroyPlugins = destroyPlugins;
    if (buttons) {
      config.registerToolbarButtons(<any>buttons);
    }

    // import('prosemirror-dev-tools').then(({ applyDevTools }) => applyDevTools(editorView));

    return {
      view: editorView,
      schema: schema,
      publicApi: publicApi,
      eventStream$: config.eventStream$,
    };
  }
}
